import React, { useRef } from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { motion, useScroll } from 'framer-motion';
import styled from 'styled-components';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import DataObjectIcon from '@mui/icons-material/DataObject';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import TouchAppIcon from '@mui/icons-material/TouchApp';

const TechnologySection = styled(Box)`
  padding: 140px 0;
  position: relative;
  background: linear-gradient(135deg, #030b17 0%, #010a14 100%);
  overflow-x: hidden;
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;

  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const BackgroundGrid = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(to right, rgba(30, 144, 255, 0.05) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(30, 144, 255, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
  z-index: 0;
`;

const ContentContainer = styled(Container)`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  padding: 0 1rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 700;
  color: #f5f5f5;
  margin-bottom: 1.5rem;
  text-align: center;
  background: linear-gradient(to right, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 5rem;
  max-width: 800px;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
`;

const TechItem = styled(Box)`
  margin-bottom: 30px;
`;

const TechImageContainer = styled(motion.div)`
  position: relative;
  height: 220px;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 20px;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.5s ease;
  }
  
  &:hover {
    img {
      transform: scale(1.1);
      filter: brightness(1.2);
    }
  }
`;

const ImageOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 100%);
  display: flex;
  align-items: flex-end;
  padding: 20px;
  
  svg {
    color: #4facfe;
    font-size: 2.5rem;
    filter: drop-shadow(0 0 10px rgba(79, 172, 254, 0.5));
    animation: rotate 10s linear infinite;
  }
  
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const TechTitle = styled(motion.h3)`
  font-size: 1.5rem;
  font-weight: 600;
  color: #f5f5f5;
  margin-bottom: 10px;
`;

const TechDescription = styled(motion.p)`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
`;

const ServiceCard = styled(motion.div)`
  background: rgba(10, 25, 41, 0.7);
  border-radius: 16px;
  padding: 30px;
  border: 1px solid rgba(79, 172, 254, 0.2);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  
  &:hover {
    border-color: #4facfe;
    box-shadow: 0 15px 40px rgba(79, 172, 254, 0.2);
  }
`;

const ServiceIcon = styled(Box)`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(79, 172, 254, 0.2), rgba(0, 242, 254, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  svg {
    color: #4facfe;
    font-size: 30px;
  }
`;

const ServiceTitle = styled(Typography)`
  font-size: 1.5rem;
  font-weight: 600;
  color: #f5f5f5;
  margin-bottom: 15px;
`;

const ServiceDescription = styled(Typography)`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin-bottom: 20px;
  flex-grow: 1;
`;

const CapabilityList = styled(Box)`
  margin-top: 20px;
  
  ul {
    padding-left: 20px;
    margin: 0;
    
    li {
      color: rgba(255, 255, 255, 0.8);
      margin-bottom: 8px;
      position: relative;
      
      &::marker {
        color: #4facfe;
      }
    }
  }
`;

const MobileContainer = styled(Box)`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    padding: 2rem 1rem;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }
`;

const techImages = [
  'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=400', // Digital transformation
  'https://images.unsplash.com/photo-1535378917042-10a22c95931a?auto=format&fit=crop&q=80&w=400', // Cybersecurity
  'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?auto=format&fit=crop&q=80&w=400', // Finance
  'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&q=80&w=400'  // Healthcare
];

const Technology = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  return (
    <TechnologySection ref={ref} id="technology">
      <BackgroundGrid />
      <ContentContainer>
        <Box display="flex" flexDirection="column" alignItems="center">
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Our Expertise
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Leveraging cutting-edge technologies and deep domain knowledge to deliver transformative solutions that address complex business challenges across multiple industries.
          </SectionSubtitle>
        </Box>

        {/* Core Service Offerings */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <ServiceCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <ServiceIcon>
                <PsychologyIcon />
              </ServiceIcon>
              <ServiceTitle variant="h3">
                AI & Data Science Excellence
              </ServiceTitle>
              <ServiceDescription>
                Our elite AI and data science team transforms raw information into actionable intelligence. We build custom machine learning models, neural networks, and predictive analytics solutions that extract hidden patterns and generate strategic insights that drive decision-making and operational excellence.
              </ServiceDescription>
              <CapabilityList>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  Key Capabilities:
                </Typography>
                <ul>
                  <li>Advanced machine learning & deep neural architectures</li>
                  <li>Natural language processing & understanding</li>
                  <li>Computer vision & intelligent image analysis</li>
                  <li>Predictive analytics & forecasting systems</li>
                  <li>Data strategy & governance frameworks</li>
                </ul>
              </CapabilityList>
            </ServiceCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ServiceCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <ServiceIcon>
                <DataObjectIcon />
              </ServiceIcon>
              <ServiceTitle variant="h3">
                Intelligent Application Development
              </ServiceTitle>
              <ServiceDescription>
                We create intuitive, AI-powered applications that redefine user experiences and automate complex processes. Our development methodology combines cutting-edge technology with human-centered design to deliver solutions that are both powerful and accessible.
              </ServiceDescription>
              <CapabilityList>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  Key Capabilities:
                </Typography>
                <ul>
                  <li>Custom enterprise software development</li>
                  <li>AI-enhanced mobile applications</li>
                  <li>Intelligent workflow automation</li>
                  <li>API integration & microservices architecture</li>
                  <li>Real-time analytics dashboards</li>
                </ul>
              </CapabilityList>
            </ServiceCard>
          </Grid>
        </Grid>
      </ContentContainer>
    </TechnologySection>
  );
};

export default Technology;